// LES PETITS SIGNES d'une personne de l'annuaire, pour la reconnaître d'un coup d'œil :
//
//   🌟 key user        — la super étoile ;
//   💪 expert métier   — le bras musclé ;
//   ★★★★★ séniorité    — 5 étoiles pour un senior, moins pour les autres niveaux ;
//   une COULEUR d'avatar par groupe (pool), toujours la même pour un même groupe.
import type { Seniority } from '../api/types';
import { initialsOf } from '../components/AppShell';
import { seniorityLabel } from '../personas/PersonaNeeds';
import { personName, type Person } from './api';
import { Chip, Avatar } from '../components/ui';
import './people.css';

/** Les étoiles de chaque niveau, sur cinq. */
export const SENIORITY_STARS: Record<Seniority, number> = { debutant: 1, confirme: 3, expert: 4, senior: 5 };

const HUES = ['cyan', 'orange', 'violet', 'pink', 'green', 'amber', 'blue', 'red'] as const;

/** La couleur d'un groupe : la même pour le même nom, partout. PUR. */
export function hueOf(group: string | null | undefined): (typeof HUES)[number] {
  if (!group) return 'cyan';
  let h = 0;
  for (const c of group) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return HUES[h % HUES.length]!;
}

export function SeniorityStars({ value }: { value: Seniority | null }) {
  if (!value) return null;
  const n = SENIORITY_STARS[value];
  return (
    <span className="ppl-stars" title={`Séniorité : ${seniorityLabel(value)}`} aria-label={`Séniorité : ${seniorityLabel(value)}, ${n} étoile${n > 1 ? 's' : ''} sur 5`}>
      {'★'.repeat(n)}<span className="ppl-stars-off">{'★'.repeat(5 - n)}</span>
    </span>
  );
}

/** La super étoile et le bras musclé. */
export function FunBadges({ person: p }: { person: Pick<Person, 'key_user' | 'domain_expert'> }) {
  return (
    <>
      {p.key_user ? <span className="ppl-fun" title="Key user" aria-label="Key user">🌟</span> : null}
      {p.domain_expert ? <span className="ppl-fun" title="Expert métier" aria-label="Expert métier">💪</span> : null}
    </>
  );
}

/** L'avatar, à la couleur de son groupe. */
export function PersonAvatar({ person: p }: { person: Pick<Person, 'first_name' | 'last_name' | 'pool'> }) {
  return <Avatar initials={initialsOf(personName(p))} seed={p.pool ?? personName(p)} size="m" title={p.pool ? `Groupe : ${p.pool}` : undefined} />;
}

/** Où elle en est : déjà invitée quelque part, ou très sollicitée ces derniers temps. */
export function SolicitationChip({ person: p, overSolicited = 3 }: { person: Person; overSolicited?: number }) {
  const a = p.active_invite;
  return (
    <>
      {a ? (
        <Chip state={a.status === 'accepted' ? 'succes' : 'avertissement'} title={a.study ?? undefined}>
          {a.status === 'accepted' && a.starts_at
            ? `RDV le ${new Date(a.starts_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`
            : 'Déjà invitée'}
          {a.study ? ` · ${a.study.length > 22 ? `${a.study.slice(0, 22)}…` : a.study}` : ''}
        </Chip>
      ) : null}
      {p.recent_invitations >= overSolicited ? (
        <Chip state="erreur" title="Invitations des 90 derniers jours">Très sollicitée · {p.recent_invitations} en 90 j</Chip>
      ) : null}
    </>
  );
}
